import { useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '@caro/shared/constants';
import { useAuth } from './useAuth';
import { usePermissions } from './usePermissions';

/**
 * Reports THIS device's OS permission status to the backend whenever it changes,
 * so the portal's tablet-permissions overview can flag tablets missing camera /
 * microphone / location / notifications.
 *
 * Why HTTP (not the MQTT heartbeat): the report goes through the authenticated
 * device-token API, so the server resolves the device from the token
 * (`req.device.id`) — no fragile userId→device topic mapping. The MQTT heartbeat
 * already carries permissions but is keyed by a topic the backend doesn't
 * ingest; this is the reliable channel.
 *
 * Sends only on actual change (the backend also de-dupes history), so this is
 * cheap. Mount once under the authenticated app shell.
 */
export function useReportPermissions(): void {
  const { api, isAuthenticated } = useAuth();
  const { permissions, allGranted, checking } = usePermissions();
  const lastSentRef = useRef<string>('');

  useEffect(() => {
    // Wait until we have a real reading (checking === false) and are authed.
    if (!isAuthenticated || checking) return;

    const missing = Object.entries(permissions)
      .filter(([, v]) => v === 'denied' || v === 'prompt')
      .map(([k]) => k);

    const snapshot = JSON.stringify({ allGranted, permissions });
    if (snapshot === lastSentRef.current) return;
    lastSentRef.current = snapshot;

    api
      .post(API_ENDPOINTS.DEVICE_PERMISSIONS_REPORT, { allGranted, missing, permissions })
      .catch(() => {
        // Allow a retry on the next change if the report failed.
        lastSentRef.current = '';
      });
  }, [isAuthenticated, checking, allGranted, permissions, api]);
}
